import React from 'react';

const Vehicle_Bazar_PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header / AppBar */}
      <header className="bg-indigo-600 text-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Privacy Policy
          </h1>
        </div>
      </header>

      {/* Main scrollable content */}
      <main className="max-w-4xl mx-auto px-5 sm:px-6 py-8 md:py-10">
        <div className="prose prose-gray max-w-none">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Privacy Policy for Vehicle Bazaar</h2>

          <p className="text-indigo-600 font-semibold text-lg mb-1">
            Powered by OneHexa Tech
          </p>

          <p className="text-gray-500 text-sm mb-10">
            Last updated: February 16, 2026
          </p>

          {/* Introduction */}
          <Section
            content={
              'Vehicle Bazaar ("we," "our," or "us") is committed to protecting your privacy. ' +
              'This Privacy Policy explains how we collect, use, disclose, and safeguard your information ' +
              'when you use our mobile application (the "App"). Please read this privacy policy carefully. ' +
              'If you do not agree with the terms of this privacy policy, please do not access the application.'
            }
          />

          {/* 1. Collection of Your Information */}
          <SectionWithIcon
            number="1"
            title="Collection of Your Information"
            icon="📝"
            content={
              'We may collect information about you in a variety of ways. The information we may collect via the App includes:\n\n' +
              'Personal Data\n' +
              'Personally identifiable information, such as your:\n\n' +
              '• Name\n' +
              '• Email address\n' +
              '• Telephone number / WhatsApp number\n' +
              '• Address (Optional)\n' +
              '• Profile Picture\n\n' +
              'We collect this information when you register specifically for the App (via Mobile OTP or Google Sign-In) or when you choose to participate in various activities related to the App, such as posting a vehicle for sale.\n\n' +
              'Derivative Data\n' +
              'Information our servers automatically collect when you access the App, such as your IP address, your native actions that are integral to the App, including liking, re-blogging, or replying to a post, as well as other interactions with the application and other users via server log files.\n\n' +
              'Mobile Device Access\n' +
              'We may request access or permission to certain features from your mobile device, including your mobile device\'s:\n\n' +
              '• Camera: To take photos of vehicles for listing.\n' +
              '• Storage / Gallery: To upload photos of vehicles and profile pictures.\n' +
              '• Internet: To connect to our servers and display content.\n\n' +
              'Financial Data\n' +
              'We do not store any financial information. Financial transactions (if any) are processed via third-party payment gateways, such as Razorpay. We encourage you to review their privacy policy and contact them directly for responses to your questions.'
            }
          />

          {/* 2. Use of Your Information */}
          <SectionWithIcon
            number="2"
            title="Use of Your Information"
            icon="💡"
            content={
              'Having accurate information about you permits us to provide you with a smooth, efficient, and customized experience. Specifically, we may use information collected about you via the App to:\n\n' +
              '• Create and manage your account.\n' +
              '• Enable user-to-user communications (e.g., contacting sellers via Phone/WhatsApp).\n' +
              '• Process vehicle listings and display them to other users.\n' +
              '• Deliver targeted advertising, coupons, newsletters, and other information regarding promotions and the App to you.\n' +
              '• Monitor and analyze usage and trends to improve your experience with the App.\n' +
              '• Perform other business activities as needed.'
            }
          />

          {/* 3. Disclosure of Your Information */}
          <SectionWithIcon
            number="3"
            title="Disclosure of Your Information"
            icon="🔗"
            content={
              'We may share information we have collected about you in certain situations. Your information may be disclosed as follows:\n\n' +
              'Third-Party Service Providers\n' +
              'We may share your information with third parties that perform services for us or on our behalf, including:\n\n' +
              '• Google AdMob: For displaying advertisements.\n' +
              '• Cloudinary: For hosting images.\n' +
              '• Razorpay: For payment processing.\n' +
              '• Google Sign-In: For authentication services.\n\n' +
              'By Law or to Protect Rights\n' +
              'If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.'
            }
          />

          {/* 4. Third-Party Websites */}
          <SectionWithIcon
            number="4"
            title="Third-Party Websites"
            icon="🌐"
            content={
              'The App may contain links to third-party websites and applications of interest, including advertisements and external services, that are not affiliated with us. Once you have used these links to leave the App, any information you provide to these third parties is not covered by this Privacy Policy, and we cannot guarantee the safety and privacy of your information.'
            }
          />

          {/* 5. Security of Your Information */}
          <SectionWithIcon
            number="5"
            title="Security of Your Information"
            icon="🛡️"
            content={
              'We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.'
            }
          />

          {/* 6. Policy for Children */}
          <SectionWithIcon
            number="6"
            title="Policy for Children"
            icon="👶"
            content={
              'We do not knowingly solicit information from or market to children under the age of 13. If you become aware of any data we have collected from children under age 13, please contact us using the contact information provided below.'
            }
          />

          {/* 7. Contact Us */}
          <Section
            number="7"
            title="Contact Us"
            content={
              <>
                If you have questions or comments about this Privacy Policy, please contact us at:
                <br /><br />
                <strong>OneHexa Tech</strong>
                <br />
                <a
                  href="mailto:onehexatech@gmail.com"
                  className="text-indigo-600 hover:underline font-medium"
                >
                  onehexatech@gmail.com
                </a>
              </>
            }
          />

          {/* Footer branding */}
          <div className="mt-16 pt-10 border-t border-gray-200 text-center text-gray-600">
            <p className="text-sm">Powered by</p>
            <p className="mt-1 text-xl font-bold text-indigo-600 tracking-wide">
              OneHexa Tech
            </p>
            <div className="h-6"></div>
          </div>
        </div>
      </main>
    </div>
  );
};

/* ── Simple text section (for shorter/plain sections) ── */
function Section({ number, title, content }) {
  return (
    <div className="mb-11 last:mb-6">
      {title && (
        <h3 className="text-xl font-semibold text-indigo-600 mb-3">
          {number}. {title}
        </h3>
      )}
      <div className="text-[15px] leading-relaxed text-gray-800 whitespace-pre-line">
        {content}
      </div>
    </div>
  );
}

/* ── Card section with icon (for key detailed sections) ── */
function SectionWithIcon({ number, title, icon, content }) {
  return (
    <div className="mb-11 last:mb-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-50 text-lg">
          {icon}
        </span>
        <h3 className="text-xl font-semibold text-indigo-600">
          {number}. {title}
        </h3>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div className="text-[14px] leading-relaxed text-gray-700 whitespace-pre-line">
          {content}
        </div>
      </div>
    </div>
  );
}

export default Vehicle_Bazar_PrivacyPolicy;
